import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  useSupportedConversionPartners,
  useQuotePartnerConversion,
  useExecutePartnerConversion,
  usePartnerConversionHistory,
  usePartnerBalances,
  useConversionApprovals,
  useRequestConversionApproval,
} from "../hooks/useTreasury";
import ConversionApprovalsPanel from "./ConversionApprovalsPanel";

const emptyForm = { partner: "", fromCurrency: "", toCurrency: "", amount: "" };

const formatBalance = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

// Phase 2 of the treasury plan - triggers a REAL conversion of Novacrust's
// own balance held at a partner. Deliberately a two-step quote-then-
// confirm flow (never a single "convert" button) - the quote step moves
// no money and can be abandoned freely; only "Confirm & Convert" is
// irreversible, and it's disabled until a quote exists and hasn't expired.
function PartnerConversionConsole() {
  const { data: supportedPartnersData } = useSupportedConversionPartners();
  const supportedPartners = supportedPartnersData || [];
  const { mutate: getQuote, isPending: isQuoting } = useQuotePartnerConversion();
  const { mutate: executeConversion, isPending: isExecuting } = useExecutePartnerConversion();
  const { data: approvedData } = useConversionApprovals("approved");
  const { mutate: requestApproval, isPending: isRequestingApproval } = useRequestConversionApproval();
  const { data: historyData, isLoading: isHistoryLoading } = usePartnerConversionHistory();
  const history = historyData || [];

  // Optional prefill from the URL (?partner=&from=&to=&amount=) - used by
  // the rebalance suggestions so one click lands here ready to quote. Only
  // pre-fills the form; a quote and an explicit confirm are still required.
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return {
      partner: (q.get("partner") || "").toUpperCase(),
      fromCurrency: (q.get("from") || "").toUpperCase(),
      toCurrency: (q.get("to") || "").toUpperCase(),
      amount: q.get("amount") || "",
    };
  });
  const [quote, setQuote] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef(null);

  // Scoped to the selected partner only - the From/To dropdowns and the
  // inline balances below are both driven by this, so a currency only
  // shows up as an option when there's an actual synced balance to show
  // next to it (the whole point of the ask: pick from what the provider
  // actually holds, see the balance right there).
  const { data: balancesData } = usePartnerBalances(form.partner || undefined, undefined, {
    enabled: Boolean(form.partner),
  });
  const latestBalanceByCurrency = useMemo(() => {
    const rows = balancesData || [];
    const seen = new Map();
    for (const row of rows) {
      if (!row.currency) continue; // an error-source row has no currency
      if (!seen.has(row.currency)) seen.set(row.currency, row);
    }
    return seen;
  }, [balancesData]);
  const currencyOptions = useMemo(
    () => Array.from(latestBalanceByCurrency.keys()).sort(),
    [latestBalanceByCurrency]
  );

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setQuote(null);
    clearInterval(timerRef.current);
    setSecondsLeft(0);
  };

  const handlePartnerChange = (partner) => {
    // Previously-selected currencies may not exist for the new partner.
    setForm({ ...emptyForm, partner });
    setQuote(null);
    clearInterval(timerRef.current);
    setSecondsLeft(0);
  };

  // Above the USD limit a second admin must approve this exact conversion
  // first; the quote response says whether that applies.
  const approvalRequired = Boolean(quote?.approval?.required);
  const matchingApproval = useMemo(() => {
    if (!approvalRequired) return null;
    const now = Date.now();
    return (
      (approvedData || []).find(
        (a) =>
          a.partner === form.partner &&
          a.fromCurrency === form.fromCurrency &&
          a.toCurrency === form.toCurrency &&
          Number(form.amount) <= Number(a.amount) &&
          (!a.expiresAt || new Date(a.expiresAt).getTime() > now)
      ) || null
    );
  }, [approvalRequired, approvedData, form]);

  const handleRequestApproval = () => {
    requestApproval({
      partner: form.partner,
      fromCurrency: form.fromCurrency,
      toCurrency: form.toCurrency,
      amount: Number(form.amount),
    });
  };

  const startExpiryCountdown = (expiryInSeconds) => {
    clearInterval(timerRef.current);
    setSecondsLeft(expiryInSeconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGetQuote = () => {
    if (!form.partner || !form.fromCurrency || !form.toCurrency || !form.amount) {
      toast.error("Partner, both currencies, and an amount are all required");
      return;
    }
    getQuote(
      {
        partner: form.partner,
        fromCurrency: form.fromCurrency,
        toCurrency: form.toCurrency,
        amount: Number(form.amount),
      },
      {
        onSuccess: (data) => {
          setQuote(data);
          startExpiryCountdown(data.expiryInSeconds || 0);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to get a quote");
        },
      }
    );
  };

  const handleConfirm = () => {
    if (!quote || secondsLeft <= 0) {
      toast.error("Quote has expired - get a fresh one before confirming");
      return;
    }
    executeConversion(
      {
        partner: form.partner,
        rateReference: quote.reference,
        fromCurrency: form.fromCurrency,
        toCurrency: form.toCurrency,
        amount: Number(form.amount),
        // BRAILS' finalize-swap response has no destination-currency
        // amount or rate field at all - the backend falls back to
        // whatever was already quoted here (locked until this quote's own
        // expiry, same window this countdown enforces). Harmless for
        // every other partner, which already return real post-conversion
        // figures from their own execute response.
        quotedToAmount: quote.toAmount,
        quotedRate: quote.rate,
        ...(matchingApproval && { approvalUuid: matchingApproval.uuid }),
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            toast.success(`Converted - new rate ${data.rate}`);
          } else {
            toast.error(`Conversion failed: ${data.failureReason || "unknown reason"}`);
          }
          setForm({ ...emptyForm, partner: form.partner });
          setQuote(null);
          clearInterval(timerRef.current);
          setSecondsLeft(0);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Conversion request failed");
        },
      }
    );
  };

  const fromBalance = form.fromCurrency ? latestBalanceByCurrency.get(form.fromCurrency) : null;
  const toBalance = form.toCurrency ? latestBalanceByCurrency.get(form.toCurrency) : null;

  return (
    <div className="space-y-8">
      <div className="flex gap-2 items-start p-4 text-sm text-amber-800 bg-amber-50 rounded-xl border border-amber-200">
        <ShieldAlert className="mt-0.5 w-4 h-4 shrink-0" />
        <p>
          This moves real money at the partner. Only providers with a confirmed, documented
          self-serve conversion endpoint are offered below - currently{" "}
          {supportedPartners.join(", ") || "none"}. Get a quote first (no money moves), then
          confirm before it expires.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 p-5 rounded-xl border border-gray-200 md:grid-cols-4">
        <div>
          <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
            Partner
          </label>
          <select
            value={form.partner}
            onChange={(e) => handlePartnerChange(e.target.value)}
            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Select partner</option>
            {supportedPartners.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
            From Currency
          </label>
          <select
            value={form.fromCurrency}
            onChange={(e) => handleChange("fromCurrency", e.target.value)}
            disabled={!form.partner}
            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
          >
            <option value="">{form.partner ? "Select currency" : "Select a partner first"}</option>
            {currencyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {fromBalance && (
            <p className="mt-1 text-xs text-gray-500">
              Balance: {formatBalance(fromBalance.balance)} {form.fromCurrency}
            </p>
          )}
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
            To Currency
          </label>
          <select
            value={form.toCurrency}
            onChange={(e) => handleChange("toCurrency", e.target.value)}
            disabled={!form.partner}
            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
          >
            <option value="">{form.partner ? "Select currency" : "Select a partner first"}</option>
            {currencyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {toBalance && (
            <p className="mt-1 text-xs text-gray-500">
              Balance: {formatBalance(toBalance.balance)} {form.toCurrency}
            </p>
          )}
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
            Amount
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) => handleChange("amount", e.target.value)}
            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="flex gap-3 items-end md:col-span-4">
          <button
            onClick={handleGetQuote}
            disabled={isQuoting}
            className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {isQuoting ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Get Quote
          </button>

          {quote && (
            <div className="flex-1 px-4 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200">
              <span className="font-semibold text-gray-800">
                {quote.fromAmount} {form.fromCurrency} → {quote.toAmount} {form.toCurrency}
              </span>{" "}
              <span className="text-gray-500">at rate {quote.rate}</span>{" "}
              <span className={secondsLeft > 0 ? "text-emerald-600" : "text-red-600"}>
                ({secondsLeft > 0 ? `expires in ${secondsLeft}s` : "expired - get a fresh quote"})
              </span>
            </div>
          )}

          {approvalRequired && !matchingApproval && (
            <button
              onClick={handleRequestApproval}
              disabled={isRequestingApproval}
              className="px-4 py-2 text-sm font-medium text-amber-900 bg-amber-100 rounded-lg hover:bg-amber-200 disabled:opacity-50"
            >
              Request approval
            </button>
          )}

          <button
            onClick={handleConfirm}
            disabled={!quote || secondsLeft <= 0 || isExecuting || (approvalRequired && !matchingApproval)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isExecuting ? "Converting..." : "Confirm & Convert"}
          </button>
        </div>

        {approvalRequired && (
          <p className="text-sm md:col-span-4 text-amber-800">
            {matchingApproval
              ? `Approved by ${matchingApproval.decidedByAdminEmail || "another admin"} - this conversion can be confirmed.`
              : `This is above $${quote.approval.thresholdUsd} (≈ $${quote.approval.amountUsd ?? "unknown"}). It needs an approval (with a code emailed to the approver) before it can run - request approval, then come back and confirm.`}
          </p>
        )}
      </div>

      <ConversionApprovalsPanel
        onUse={(a) => {
          setQuote(null);
          setForm({
            partner: a.partner,
            fromCurrency: a.fromCurrency,
            toCurrency: a.toCurrency,
            amount: String(Number(a.amount)),
          });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      <div>
        <h2 className="mb-3 text-sm font-bold tracking-wider text-gray-500 uppercase">
          Conversion History
        </h2>
        {isHistoryLoading ? (
          <p className="flex gap-2 items-center text-sm text-gray-500">
            Loading <Loader className="w-4 h-4 animate-spin" />
          </p>
        ) : (
          <div className="overflow-hidden rounded border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  {["Partner", "From", "To", "Rate", "Status", "Type", "Initiated By", "Date"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.length > 0 ? (
                  history.map((row) => (
                    <tr key={row.uuid}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-700">{row.partner}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {row.fromAmount} {row.fromCurrency}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {Number(row.toAmount) > 0 ? `${row.toAmount} ${row.toCurrency}` : <span title="Not recorded">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {Number(row.rate) > 0 ? row.rate : <span title="Not recorded">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            row.status === "success"
                              ? "bg-emerald-100 text-emerald-800"
                              : row.status === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {row.origin === "automatic" ? (
                          <span
                            className="px-2 py-0.5 text-xs font-semibold text-violet-800 bg-violet-100 rounded-full"
                            title={`Run automatically to cover a payout${row.payoutReference ? ` (payout reference ${row.payoutReference})` : ""}`}
                          >
                            Automatic
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                            Manual
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {row.origin === "automatic" ? (
                          <span className="font-mono text-xs text-gray-500">{row.payoutReference || "payout"}</span>
                        ) : (
                          row.initiatedByAdminEmail || "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-gray-500">
                      No conversions triggered yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PartnerConversionConsole;
